import React, { useCallback, useMemo } from 'react';
import { Row } from '@ydistri/ds';
import StoreSelector, { StoreOption } from './StoreSelector';
import { appendStoreDecile } from './appendStoreDecile';
import { useSearchParams } from 'react-router';
import { useDetail } from '../../../../hooks/useDetail';
import { ListType, useGetRegionsQuery, useGetStoresQuery } from '../../../../../../apis/apiLists';
import { useDisplayStoreLabelFormatter } from '../../../../../../hooks/useDisplayStoreLabel';
import { useTemplateOrCalculationWithValue } from '../../../../../../hooks/useTemplateOrCalculationWithValue';
import { RegionResponse } from '@ydistri/api-sdk';

interface StoreSelectorControllerProps {
  onChange?: (value: number) => void;
}

const getNoSelectionLabel = (
  regionId: number,
  regionEntities: ListType<RegionResponse> | undefined,
): string | undefined => {
  if (regionId !== -1) {
    const region = regionEntities?.object[regionId];
    if (region) {
      return `All stores in ${region.name}`;
    }
  }
};

const StoreSelectorController: React.FC<StoreSelectorControllerProps> = ({ onChange }) => {
  const forcedTemplate = useTemplateOrCalculationWithValue('Template');
  const { data: stores } = useGetStoresQuery(forcedTemplate.value);

  const { storeId, regionId } = useDetail('storeId', 'regionId');
  const { data: regionEntities } = useGetRegionsQuery(forcedTemplate.value);
  const [, setSearchParams] = useSearchParams();
  const storeLabelFormatter = useDisplayStoreLabelFormatter();

  const storesOptions: StoreOption[] = useMemo(() => {
    if (!stores) {
      return [];
    }

    const regionStores =
      regionId !== -1 ? stores.array.filter(store => store.region?.id === regionId) : stores.array;

    return regionStores
      .map(store => ({
        id: store.id,
        name: store.name,
        code: store.code ?? null,
        customerStoreId: store.customerStoreId ?? null,
        label: appendStoreDecile(
          storeLabelFormatter(store.name, store.customerStoreId, store.code),
          store.storeDecile,
        ),
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [regionId, storeLabelFormatter, stores]);

  const changeHandler = useCallback(
    (newValue: number) => {
      setSearchParams(prevParams => {
        if (newValue !== -1) {
          prevParams.set('store', newValue.toString());
        } else {
          prevParams.delete('store');
        }
        return prevParams;
      });

      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange, setSearchParams],
  );

  const noSelectionLabel = getNoSelectionLabel(regionId, regionEntities);

  return (
    <Row $gap="0.5rem" $alignItems="center">
      <StoreSelector
        options={storesOptions}
        onChange={changeHandler}
        value={storeId}
        noSelectionLabel={noSelectionLabel}
      />
    </Row>
  );
};

export default StoreSelectorController;
